import { Input } from "@/components/ui/input"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useState } from "react"
import { usePatchSupplier, useSupplierDetail } from "../../../api/suppliersApi"
import type {
  SupplierOut,
  SupplierUpdateIn,
} from "../../../api/types/masterData"
import { intlLocale } from "../../../utils/date"
import { AdditionalAttributes } from "../../shared/AdditionalAttributes"
import { CollapsibleSection } from "../../shared/CollapsibleSection"
import { EditActionBar } from "../../shared/EditActionBar"
import { FieldsGrid } from "../../shared/FieldsGrid"
import { FieldsSectionSkeleton } from "../../shared/FieldsSectionSkeleton"
import { SectionLabel } from "../../shared/SectionLabel"
import { EntityFieldsHeader } from "../EntityFieldsHeader"
import {
  useEntityFieldsState,
  type EntityFieldsState,
} from "../useEntityFieldsState"
import { useScrollIntoViewOnEdit } from "../useScrollIntoViewOnEdit"

interface SupplierFieldsState
  extends Omit<EntityFieldsState<SupplierOut, SupplierUpdateIn>, "entity"> {
  supplier: SupplierOut | undefined
}

export function useSupplierFieldsState(): SupplierFieldsState {
  const { entity: supplier, ...rest } = useEntityFieldsState(
    useSupplierDetail,
    usePatchSupplier,
  )
  return { supplier, ...rest }
}

interface SectionProps {
  state: SupplierFieldsState
}

export function SupplierFieldsHeader({ state }: SectionProps) {
  return (
    <EntityFieldsHeader
      entity={state.supplier}
      isLoading={state.isLoading}
      editing={state.editing}
      setEditing={state.setEditing}
      labelKey="MC_MD_TAB_SUPPLIERS"
      name={state.supplier?.name}
      subtitle={state.supplier?.supplierNumber}
    />
  )
}

export function SupplierFieldsBody({ state }: SectionProps) {
  const { translate, currentLanguage } = useTranslation()
  const {
    supplier,
    isLoading,
    editing,
    edits,
    setEdits,
    isPending,
    handleSave,
    handleCancel,
  } = state
  const [fieldsOpen, setFieldsOpen] = useState(false)
  const fieldsRef = useScrollIntoViewOnEdit<HTMLDivElement>(editing)

  if (isLoading) return <FieldsSectionSkeleton count={5} />
  if (!supplier) return null

  return (
    <div ref={fieldsRef} className="flex flex-col">
      <CollapsibleSection
        label={translate("MC_MD_FIELDS_SECTION")}
        open={editing || fieldsOpen}
        onOpenChange={setFieldsOpen}
      >
        <FieldsGrid
          fields={[
            // A supplier name is long ("AVNET Abacus ...") and in a half-width
            // cell its input kept ~230px, so the name was cut off while
            // editing. Full width, and first: the grid auto-places, so a
            // full-width row after an odd number of half fields leaves an
            // empty cell. Keep the half fields paired between the two
            // full-width rows (Name, E-Mail).
            {
              label: translate("MC_MD_FIELD_NAME"),
              value: editing ? (
                <Input
                  value={edits.name ?? supplier.name}
                  onChange={(e) =>
                    setEdits((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              ) : (
                supplier.name
              ),
              fullWidth: true,
            },
            {
              label: translate("MC_MD_FIELD_SUPPLIER_NUMBER"),
              value: (
                <span className="font-mono">{supplier.supplierNumber}</span>
              ),
            },
            {
              label: translate("MC_MD_FIELD_COUNTRY"),
              value: editing ? (
                <Input
                  value={edits.country ?? supplier.country ?? ""}
                  onChange={(e) =>
                    setEdits((prev) => ({ ...prev, country: e.target.value }))
                  }
                  className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              ) : (
                (supplier.country ?? "—")
              ),
            },
            {
              label: translate("MC_MD_FIELD_VAT_ID"),
              value: <span className="font-mono">{supplier.vatId ?? "—"}</span>,
            },
            {
              label: translate("MC_MD_FIELD_DUNS"),
              value: editing ? (
                <Input
                  value={edits.dunsNumber ?? supplier.dunsNumber ?? ""}
                  onChange={(e) =>
                    setEdits((prev) => ({
                      ...prev,
                      dunsNumber: e.target.value,
                    }))
                  }
                  className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              ) : (
                <span className="font-mono">{supplier.dunsNumber ?? "—"}</span>
              ),
            },
            {
              label: translate("MC_MD_FIELD_EMAIL"),
              value: editing ? (
                <Input
                  value={edits.email ?? supplier.email ?? ""}
                  onChange={(e) =>
                    setEdits((prev) => ({ ...prev, email: e.target.value }))
                  }
                  className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              ) : (
                (() => {
                  if (supplier.email) return supplier.email
                  const fromContact = supplier.contacts.find(
                    (c) => c.email,
                  )?.email
                  if (fromContact) {
                    return (
                      <span className="inline-flex items-center gap-1">
                        {fromContact}
                        <span className="rounded bg-grey3 px-1 text-[10px] text-grey5">
                          {translate("MC_MD_BADGE_FROM_CONTACTS")}
                        </span>
                      </span>
                    )
                  }
                  return "—"
                })()
              ),
              // AN ADDRESS IS LONG AND ONE PIECE. In the two-column grid the value
              // cell keeps ~180px after the fixed label column, so
              // "info@baumann-printsolutions.de" truncated to
              // "info@baumann-printsol…" — and a half address is not a smaller
              // address, it is the wrong one. Spanning the row is what `fullWidth`
              // was added for; it had never been used.
              fullWidth: true,
              // The value is a NODE whenever the address came from a contact (it
              // carries a badge), and the grid's title fallback only reads plain
              // strings. Set explicitly so the hover text survives that case too.
              title:
                supplier.email ??
                supplier.contacts.find((c) => c.email)?.email ??
                undefined,
            },
            {
              label: translate("MC_MD_FIELD_DECLARATION_VALIDITY"),
              value: editing ? (
                <Input
                  type="number"
                  value={
                    edits.declarationValidityDays ??
                    supplier.declarationValidityDays ??
                    ""
                  }
                  onChange={(e) =>
                    setEdits((prev) => ({
                      ...prev,
                      declarationValidityDays: e.target.value
                        ? Number(e.target.value)
                        : undefined,
                    }))
                  }
                  className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              ) : (
                <span className="font-mono">
                  {supplier.declarationValidityDays != null
                    ? translate("MC_MD_UNIT_DAYS", {
                        count: supplier.declarationValidityDays,
                      })
                    : "—"}
                </span>
              ),
            },
            {
              label: translate("MC_MD_FIELD_LAST_DECLARATION"),
              value: (
                <span className="font-mono">
                  {supplier.lastDeclarationAt
                    ? new Date(supplier.lastDeclarationAt).toLocaleDateString(
                        intlLocale(currentLanguage),
                      )
                    : "—"}
                </span>
              ),
            },
          ]}
        />

        {supplier.contacts.length > 0 && (
          <div className="mt-4 px-4 pb-4">
            <SectionLabel className="mb-3">
              {translate("MC_MD_SECTION_CONTACTS")}
            </SectionLabel>
            <FieldsGrid
              fields={supplier.contacts.map((c) => ({
                label: c.role ?? translate("MC_MD_CONTACT_ROLE_FALLBACK"),
                value: [c.name, c.email].filter(Boolean).join(" · "),
              }))}
            />
          </div>
        )}
      </CollapsibleSection>

      {supplier.additionalAttributes && (
        <AdditionalAttributes attributes={supplier.additionalAttributes} />
      )}

      {editing && (
        <EditActionBar
          onCancel={handleCancel}
          onSave={handleSave}
          isPending={isPending}
        />
      )}
    </div>
  )
}
